import React, { useState, useEffect, useCallback } from 'react';
import { getAgencyClients } from '../../services/firestoreService';

// Verification status config
const VERIFY_STATUS = {
  PHONE_VERIFIED: { label: 'Phone Verified', bg: '#4ADE8025', color: '#16a34a', border: '#4ADE80' },
  PHONE_UNCONFIRMED: { label: 'Unconfirmed', bg: '#FBBF2425', color: '#ca8a04', border: '#FBBF24' },
  NEEDS_ALT_CONTACT: { label: 'Dead Phone', bg: '#F8717125', color: '#dc2626', border: '#F87171' },
  REMOVED: { label: 'Removed', bg: '#9ca3af20', color: '#6b7280', border: '#9ca3af' },
};

// Static prospect data (will connect to Firestore later)
const PROSPECTS = [
  {
    id: 'p1',
    business: 'Minutemen Land Services',
    owner: 'Jordan Grant',
    phone: '+12032477744',
    phoneDisplay: '(203) 247-7744',
    industry: 'Landscaping',
    verifyStatus: 'PHONE_VERIFIED',
    demoSite: 'https://apn-demos.vercel.app/minutemen-land-services',
    town: 'Norwalk',
    wave: 1,
    notes: 'Retainer sent 3/30, NOT PAID',
    overdue: true,
  },
  {
    id: 'p2',
    business: 'A to Z Property Maintenance',
    owner: 'Daimeon Baity',
    phone: '+12037759174',
    phoneDisplay: '(203) 775-9174',
    industry: 'Property Maintenance',
    verifyStatus: 'PHONE_VERIFIED',
    demoSite: 'https://apn-demos.vercel.app/a-to-z-property-maintenance-llc',
    town: 'Brookfield',
    wave: 1,
    notes: '',
    overdue: false,
  },
  {
    id: 'p3',
    business: 'Elite Gutter Solutions',
    owner: 'Marcus Webb',
    phone: '+12034459988',
    phoneDisplay: '(203) 445-9988',
    industry: 'Gutters',
    verifyStatus: 'PHONE_UNCONFIRMED',
    demoSite: 'https://apn-demos.vercel.app/elite-gutter-solutions',
    town: 'Stamford',
    wave: 1,
    notes: 'No answer 3x, try again',
    overdue: false,
  },
  {
    id: 'p4',
    business: 'Premier Paving CT',
    owner: 'Dave Kowalski',
    phone: '+12036781234',
    phoneDisplay: '(203) 678-1234',
    industry: 'Paving',
    verifyStatus: 'PHONE_VERIFIED',
    demoSite: 'https://apn-demos.vercel.app/premier-paving-ct',
    town: 'Danbury',
    wave: 1,
    notes: '',
    overdue: false,
  },
  {
    id: 'p5',
    business: 'Tri-State Roofing',
    owner: 'Anthony Russo',
    phone: '+12039995544',
    phoneDisplay: '(203) 999-5544',
    industry: 'Roofing',
    verifyStatus: 'NEEDS_ALT_CONTACT',
    demoSite: 'https://apn-demos.vercel.app/tri-state-roofing',
    town: 'Bridgeport',
    wave: 2,
    notes: 'Number disconnected',
    overdue: false,
  },
  {
    id: 'p6',
    business: 'Clean Cut Lawn Care',
    owner: 'Tyler Morgan',
    phone: '+12038871122',
    phoneDisplay: '(203) 887-1122',
    industry: 'Lawn Care',
    verifyStatus: 'PHONE_UNCONFIRMED',
    demoSite: 'https://apn-demos.vercel.app/clean-cut-lawn-care',
    town: 'Milford',
    wave: 2,
    notes: 'Voicemail left',
    overdue: false,
  },
  {
    id: 'p7',
    business: 'CT Deck Builders',
    owner: 'Brian Sullivan',
    phone: '+12035563300',
    phoneDisplay: '(203) 556-3300',
    industry: 'Decks',
    verifyStatus: 'PHONE_VERIFIED',
    demoSite: 'https://apn-demos.vercel.app/ct-deck-builders',
    town: 'Fairfield',
    wave: 2,
    notes: '',
    overdue: false,
  },
];

const SCRAPER_DATA = {
  currentWave: 2,
  totalWaves: 5,
  townsDone: ['Norwalk', 'Stamford', 'Bridgeport', 'Danbury', 'Brookfield', 'Fairfield', 'Milford'],
  townsRemaining: ['New Haven', 'Hartford', 'Waterbury', 'Greenwich', 'Westport', 'Shelton', 'Trumbull', 'Monroe'],
  newThisWeek: 4,
};

const CLIENT_TIERS = {
  starter: { name: 'Starter', monthly: 79, color: '#4ADE80' },
  professional: { name: 'Professional', monthly: 199, color: '#60A5FA' },
  premium: { name: 'Premium', monthly: 399, color: '#A78BFA' },
};

export default function AgencyPipeline() {
  const [prospects, setProspects] = useState(PROSPECTS);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAgencyClients();
      setClients(data);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClients([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const refresh = () => { loadData(); setProspects([...PROSPECTS]); };

  // Computed counts
  const verified = prospects.filter(p => p.verifyStatus === 'PHONE_VERIFIED');
  const unconfirmed = prospects.filter(p => p.verifyStatus === 'PHONE_UNCONFIRMED');
  const deadPhone = prospects.filter(p => p.verifyStatus === 'NEEDS_ALT_CONTACT');
  const overdue = prospects.filter(p => p.overdue);
  const activeProspects = prospects.filter(p => p.verifyStatus !== 'REMOVED');
  const totalMRR = clients.filter(c => c.monthlyActive).reduce((sum, c) => sum + (CLIENT_TIERS[c.tier]?.monthly || 0), 0);
  const pipelineValue = activeProspects.length * 79; // Estimated at starter tier

  const markInterested = (id) => {
    setProspects(prev => prev.map(p => p.id === id ? { ...p, notes: (p.notes ? p.notes + ' | ' : '') + 'INTERESTED ' + new Date().toLocaleDateString() } : p));
    alert('Marked as interested!');
  };

  const sendFollowUp = (id) => {
    const prospect = prospects.find(p => p.id === id);
    if (prospect) {
      alert(`Follow-up queued for ${prospect.business} (${prospect.owner})`);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '40px 16px', textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: '18px', fontWeight: '500' }}>Loading pipeline...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '16px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '12px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: 0 }}>Agency Pipeline</h1>
          <p style={{ color: '#6b7280', fontSize: '13px', marginTop: '4px' }}>
            Last refreshed: {lastRefresh.toLocaleTimeString()}
          </p>
        </div>
        <button onClick={refresh} style={{
          background: '#111827', color: '#fff', border: 'none', borderRadius: '8px',
          padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer',
        }}>
          Refresh
        </button>
      </div>

      {/* NUMBERS BAR */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {[
          { label: 'Total Prospects', value: activeProspects.length, color: '#111827', topColor: '#6b7280' },
          { label: 'Phone Verified', value: verified.length, color: '#16a34a', topColor: '#4ADE80' },
          { label: 'Unconfirmed', value: unconfirmed.length, color: '#ca8a04', topColor: '#FBBF24' },
          { label: 'Current MRR', value: `$${totalMRR}`, color: '#2563eb', topColor: '#60A5FA' },
          { label: 'Pipeline Value', value: `$${pipelineValue}`, color: '#7c3aed', topColor: '#A78BFA' },
        ].map(item => (
          <div key={item.label} style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: '10px',
            padding: '14px', borderTop: `3px solid ${item.topColor}`, textAlign: 'center',
          }}>
            <div style={{ fontSize: '11px', color: '#6b7280', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '4px' }}>{item.label}</div>
            <div style={{ fontSize: '22px', fontWeight: '700', color: item.color }}>{item.value}</div>
          </div>
        ))}
      </div>

      {/* NEEDS ATTENTION - Red section */}
      {(overdue.length > 0 || verified.length > 0 || unconfirmed.length > 0) && (
        <div style={{
          background: '#fef2f2', border: '2px solid #fca5a5', borderRadius: '12px',
          padding: '20px', marginBottom: '24px',
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#dc2626', margin: '0 0 16px 0' }}>
            NEEDS ATTENTION
          </h2>

          {/* Overdue Payments */}
          {overdue.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#991b1b', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '8px' }}>
                Overdue Payments ({overdue.length})
              </div>
              {overdue.map(p => (
                <div key={p.id} style={{
                  background: '#fff', borderRadius: '8px', padding: '12px 16px', marginBottom: '6px',
                  border: '1px solid #fca5a5', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                }}>
                  <div>
                    <span style={{ fontWeight: '600', color: '#111827' }}>{p.owner}</span>
                    <span style={{ color: '#dc2626', fontSize: '13px' }}> -- {p.notes}</span>
                  </div>
                  <a href={`tel:${p.phone}`} style={{
                    background: '#dc2626', color: '#fff', padding: '6px 14px', borderRadius: '6px',
                    fontSize: '13px', fontWeight: '600', textDecoration: 'none',
                  }}>
                    Call Now
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Phone Verified Ready for Outreach */}
          {verified.length > 0 && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#16a34a', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '8px' }}>
                Phone Verified - Ready for Outreach ({verified.length})
              </div>
              {verified.filter(p => !p.overdue).map(p => (
                <div key={p.id} style={{
                  background: '#fff', borderRadius: '8px', padding: '10px 14px', marginBottom: '4px',
                  border: '1px solid #bbf7d0', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: '8px',
                }}>
                  <div style={{ fontSize: '14px' }}>
                    <span style={{ fontWeight: '600', color: '#111827' }}>{p.business}</span>
                    <span style={{ color: '#6b7280' }}> -- {p.owner}</span>
                  </div>
                  <a href={`tel:${p.phone}`} style={{
                    color: '#16a34a', fontWeight: '600', fontSize: '13px', textDecoration: 'none',
                  }}>
                    {p.phoneDisplay}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Unconfirmed */}
          {unconfirmed.length > 0 && (
            <div>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#ca8a04', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: '8px' }}>
                Phone Unconfirmed - Retry Needed ({unconfirmed.length})
              </div>
            </div>
          )}
        </div>
      )}

      {/* PROSPECT PIPELINE */}
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 16px 0', paddingBottom: '10px', borderBottom: '2px solid #e5e7eb' }}>
        Prospect Pipeline
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px', marginBottom: '32px' }}>
        {prospects.filter(p => p.verifyStatus !== 'REMOVED').map(p => {
          const status = VERIFY_STATUS[p.verifyStatus];
          return (
            <div key={p.id} style={{
              background: '#fff', borderRadius: '12px', padding: '18px',
              border: `1px solid #e5e7eb`, borderLeft: `5px solid ${status.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,.04)',
            }}>
              {/* Business name + badge */}
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '10px' }}>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>{p.business}</div>
                <span style={{
                  padding: '3px 10px', borderRadius: '100px', fontSize: '11px', fontWeight: '600',
                  background: status.bg, color: status.color, whiteSpace: 'nowrap',
                }}>
                  {status.label}
                </span>
              </div>

              {/* Owner */}
              <div style={{ fontSize: '14px', color: '#374151', marginBottom: '4px' }}>{p.owner}</div>

              {/* Phone - click to call */}
              <a href={`tel:${p.phone}`} style={{
                display: 'inline-block', fontSize: '14px', color: '#2563eb', fontWeight: '600',
                textDecoration: 'none', marginBottom: '6px',
              }}>
                {p.phoneDisplay}
              </a>

              {/* Industry + Town */}
              <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '8px' }}>
                {p.industry} -- {p.town}
              </div>

              {/* Notes */}
              {p.notes && (
                <div style={{
                  fontSize: '12px', color: p.overdue ? '#dc2626' : '#6b7280',
                  fontWeight: p.overdue ? '600' : '400',
                  background: p.overdue ? '#fef2f2' : '#f9fafb',
                  padding: '6px 10px', borderRadius: '6px', marginBottom: '10px',
                }}>
                  {p.notes}
                </div>
              )}

              {/* Demo site link */}
              <a href={p.demoSite} target="_blank" rel="noopener noreferrer" style={{
                display: 'inline-block', fontSize: '12px', color: '#7c3aed', marginBottom: '12px',
                textDecoration: 'none',
              }}>
                View Demo Site
              </a>

              {/* Action buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <button onClick={() => markInterested(p.id)} style={{
                  flex: '1 1 auto', background: '#4ADE80', color: '#fff', border: 'none',
                  borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', minWidth: '120px',
                }}>
                  Mark Interested
                </button>
                <button onClick={() => sendFollowUp(p.id)} style={{
                  flex: '1 1 auto', background: '#2563eb', color: '#fff', border: 'none',
                  borderRadius: '8px', padding: '8px 12px', fontSize: '13px', fontWeight: '600',
                  cursor: 'pointer', minWidth: '120px',
                }}>
                  Send Follow-up
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* CLIENT STATUS */}
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 16px 0', paddingBottom: '10px', borderBottom: '2px solid #e5e7eb' }}>
        Client Status
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px', marginBottom: '32px' }}>
        {/* Active Clients */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>Active Clients</div>
          {clients.filter(c => c.status === 'active' || c.monthlyActive).length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>No active clients yet</div>
          )}
          {clients.filter(c => c.status === 'active' || c.monthlyActive).map(c => {
            const tier = CLIENT_TIERS[c.tier];
            return (
              <div key={c.id} style={{
                padding: '12px', borderRadius: '8px', border: `1px solid ${tier?.color || '#e5e7eb'}20`,
                background: `${tier?.color || '#f3f4f6'}08`, marginBottom: '8px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                  <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>{c.name}</span>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: tier?.color || '#6b7280' }}>
                    {tier?.name} -- ${tier?.monthly}/mo
                  </span>
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280' }}>{c.owner}</div>
                {c.phone && (
                  <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`} style={{
                    fontSize: '13px', color: '#2563eb', textDecoration: 'none',
                  }}>
                    {c.phone}
                  </a>
                )}
              </div>
            );
          })}
        </div>

        {/* Warm Leads */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '14px' }}>Warm Leads</div>
          {clients.filter(c => ['interested', 'contract_sent', 'onboarding'].includes(c.status)).length === 0 && (
            <div style={{ color: '#9ca3af', fontSize: '13px' }}>No warm leads in CRM</div>
          )}
          {clients.filter(c => ['interested', 'contract_sent', 'onboarding'].includes(c.status)).map(c => (
            <div key={c.id} style={{
              padding: '12px', borderRadius: '8px', border: '1px solid #FBBF2430',
              background: '#FBBF2408', marginBottom: '8px',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontWeight: '600', fontSize: '14px', color: '#111827' }}>{c.name}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: '100px', fontSize: '11px', fontWeight: '600',
                  background: c.status === 'contract_sent' ? '#FBBF2420' : '#60A5FA20',
                  color: c.status === 'contract_sent' ? '#ca8a04' : '#2563eb',
                }}>
                  {c.status === 'contract_sent' ? 'Contract Sent' : c.status === 'interested' ? 'Interested' : 'Onboarding'}
                </span>
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280' }}>{c.owner}</div>
              {c.phone && (
                <a href={`tel:${c.phone.replace(/[^\d+]/g, '')}`} style={{
                  fontSize: '13px', color: '#2563eb', textDecoration: 'none',
                }}>
                  {c.phone}
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SCRAPER PROGRESS */}
      <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', margin: '0 0 16px 0', paddingBottom: '10px', borderBottom: '2px solid #e5e7eb' }}>
        Scraper Progress
      </h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '14px', marginBottom: '32px' }}>
        {/* Wave progress */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Wave Progress</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Current Wave</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>
              {SCRAPER_DATA.currentWave} / {SCRAPER_DATA.totalWaves}
            </span>
          </div>
          {/* Progress bar */}
          <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '8px', marginBottom: '14px' }}>
            <div style={{
              background: '#4ADE80', borderRadius: '4px', height: '8px',
              width: `${(SCRAPER_DATA.currentWave / SCRAPER_DATA.totalWaves) * 100}%`,
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Towns Done</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>{SCRAPER_DATA.townsDone.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Towns Remaining</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#ca8a04' }}>{SCRAPER_DATA.townsRemaining.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>New This Week</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#2563eb' }}>{SCRAPER_DATA.newThisWeek}</span>
          </div>
        </div>

        {/* Towns completed */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Towns Completed</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {SCRAPER_DATA.townsDone.map(t => (
              <span key={t} style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
                background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0',
              }}>
                {t}
              </span>
            ))}
          </div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: '#6b7280', marginTop: '14px', marginBottom: '6px' }}>Remaining</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {SCRAPER_DATA.townsRemaining.map(t => (
              <span key={t} style={{
                padding: '4px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: '500',
                background: '#f9fafb', color: '#9ca3af', border: '1px solid #e5e7eb',
              }}>
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Pipeline summary */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '18px' }}>
          <div style={{ fontSize: '15px', fontWeight: '700', color: '#111827', marginBottom: '12px' }}>Pipeline Summary</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Total in Pipeline</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#111827' }}>{activeProspects.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', color: '#16a34a' }}>Phone Verified</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#16a34a' }}>{verified.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', color: '#ca8a04' }}>Unconfirmed</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#ca8a04' }}>{unconfirmed.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
            <span style={{ fontSize: '13px', color: '#dc2626' }}>Dead Phone</span>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626' }}>{deadPhone.length}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
            <span style={{ fontSize: '13px', color: '#dc2626', fontWeight: '600' }}>Overdue Payment</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#dc2626' }}>{overdue.length}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
